import React, { useState, useMemo, useRef } from 'react';
import { LocationData, Floor, RackType, ServerType, TORSwitchType, GameNotification, PendingDelivery } from '../types';
import { SERVER_TYPES, IXP_EXCHANGES } from '../constants';
import GameHeader from './GameHeader';
import ManagementPanel from './ManagementPanel';
import FloorSelector from './FloorSelector';
import WelcomeMessage from './WelcomeMessage';
import RackGrid from './RackGrid';
import ServerManagement from './ServerManagement';
import MmrView from './MmrView';
import SetupChecklist from './SetupChecklist';
import NotificationSystem from './NotificationSystem';
import FloorManagement from './FloorManagement';
import ResourceStatusDisplay from './ResourceStatusDisplay';
import { computeUnlockSignals } from '../utils/unlockSignals';

interface DatacenterViewProps {
  currentLocationData: LocationData;
  currentFloorData: Floor | null;
  selectedRack: number | null;
  setSelectedRack: (rackId: number | null) => void;
  selectedRackObj: any;
  selectedRackType: RackType | null;
  selectedRackUsedPower: number;
  rackTypes: RackType[];
  serverTypes: ServerType[];
  torSwitchTypes: TORSwitchType[];
  playerLevel: number;
  money: number;
  currentLocation: string;
  buyRack: (locationId: string, rackId: number | null, rackTypeId: string) => void;
  installServer: (locationId: string, rackId: number | null, serverTypeId: string) => void;
  fillRack: (locationId: string, rackId: number, serverTypeId: string) => number;
  installTORSwitch: (locationId: string, rackId: number | null, switchTypeId: string) => void;
  installMpoPatchPanel: (locationId: string, rackId: number | null, panelTypeId: string) => void;
  upgradeMpoPatchPanel: (locationId: string, rackId: number | null, panelTypeId: string) => void;
  installRedundantTorSwitch: (locationId: string, rackId: number | null, switchTypeId: string) => void;
  installManagementSwitch: (locationId: string, rackId: number | null, switchTypeId: string) => void;
  removeServer: (locationId: string, rackId: number, serverId: string) => void;
  removeRack: (locationId: string, rackId: number) => void;
  repairServer: (locationId: string, rackId: number, serverId: string) => void;
  gameTime: number;
  notifications: GameNotification[];
  removeNotification: (id: string) => void;
  setShowUpgrades: (show: boolean) => void;
  setShowPowerManagementModal: (show: boolean) => void;
  setShowGoals: (show: boolean) => void;
  setShowChillerManagementModal: (show: boolean) => void;
  currentFloor: string;
  setCurrentFloor: (floorKey: string) => void;
  setShowFloorDesigner: (show: boolean) => void;
  openDesignerForEdit: (floorId: string) => void;
  formatCurrency: (amount: number, decimals?: number) => string;
  locations: Record<string, LocationData>;
  upgradeFloorQuality: (locationId: string, floorId: string, floorQualityId: string) => boolean;
  getFloorCoolingEfficiency: (floorId: string, locationId: string) => number;
  gameSpeed: number;
  setGameSpeed: (speed: number) => void;
  isPaused: boolean;
  setIsPaused: (paused: boolean) => void;
  reputation: number;
  powerEfficiencyUpgrade: number;
  setShowSiteLayoutEditor: (show: boolean) => void;
  setShowStaffModal: (show: boolean) => void;
  setShowNetworkModal: (show: boolean) => void;
  setShowMmrModal: (show: boolean) => void;
  setShowFinancials: (show: boolean) => void;
  setShowFloorModal: (show: boolean) => void;
  setShowBackboneModal: (show: boolean) => void;
  setShowContractsModal: (show: boolean) => void;
  setShowGreenEnergyModal: (show: boolean) => void;
  setShowLoansModal: (show: boolean) => void;
  setShowFleetOverview?: (show: boolean) => void;
  activeContractCount?: number;
  activeLoanCount?: number;
  activeContracts?: import('../types').ActiveContract[];
  pendingDeliveries?: PendingDelivery[];
  gameDay?: number;
  buildMmr: (locationId: string) => void;
  installCoreSwitch: (locationId: string, switchTypeId: string) => void;
  staffCount?: number;
  serverPerformanceUpgrade?: number;
  onOpenSaveModal: () => void;
  onResetGame: () => void;
  onOpenEventLog: () => void;
  eventLogUnread: number;
  manageUnlockPulse?: boolean;
  onManageOpened?: () => void;
}

const DatacenterView: React.FC<DatacenterViewProps> = ({
  currentLocationData,
  currentFloorData,
  selectedRack,
  setSelectedRack,
  selectedRackObj,
  selectedRackType,
  selectedRackUsedPower,
  rackTypes,
  serverTypes,
  torSwitchTypes,
  playerLevel,
  money,
  currentLocation,
  buyRack,
  installServer,
  fillRack,
  installTORSwitch,
  installMpoPatchPanel,
  upgradeMpoPatchPanel,
  installRedundantTorSwitch,
  installManagementSwitch,
  removeServer,
  removeRack,
  repairServer,
  gameTime,
  notifications,
  removeNotification,
  setShowUpgrades,
  setShowPowerManagementModal,
  setShowGoals,
  setShowChillerManagementModal,
  currentFloor,
  setCurrentFloor,
  setShowFloorDesigner,
  openDesignerForEdit,
  formatCurrency,
  locations,
  upgradeFloorQuality,
  getFloorCoolingEfficiency,
  gameSpeed,
  setGameSpeed,
  isPaused,
  setIsPaused,
  reputation,
  powerEfficiencyUpgrade,
  setShowSiteLayoutEditor,
  setShowStaffModal,
  setShowNetworkModal,
  setShowMmrModal,
  setShowFinancials,
  setShowFloorModal,
  setShowBackboneModal,
  setShowContractsModal,
  setShowGreenEnergyModal,
  setShowLoansModal,
  setShowFleetOverview,
  activeContractCount = 0,
  activeLoanCount = 0,
  activeContracts = [],
  pendingDeliveries = [],
  gameDay = 0,
  buildMmr,
  installCoreSwitch,
  staffCount = 0,
  serverPerformanceUpgrade = 0,
  onOpenSaveModal,
  onResetGame,
  onOpenEventLog,
  eventLogUnread,
  manageUnlockPulse = false,
  onManageOpened,
}) => {
  const [panelOpen, setPanelOpen] = useState(false);
  const [showMmrView, setShowMmrView] = useState(false);

  // Progressive-disclosure signals for the Management panel — advanced tools stay
  // dimmed+locked until they become relevant.
  const unlockSignals = useMemo(
    () => computeUnlockSignals(locations, activeContracts),
    [locations, activeContracts]
  );

  // Guided-setup helpers: select the right rack for the current step and scroll
  // the server/network panel into view (with a brief highlight) so new players
  // never have to hunt for where to click next.
  const serverPanelRef = useRef<HTMLDivElement>(null);
  const [panelFlash, setPanelFlash] = useState(false);

  const focusServerPanel = () => {
    serverPanelRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    setPanelFlash(true);
    setTimeout(() => setPanelFlash(false), 1600);
  };
  const selectRackForPurchase = () => {
    const empty = (currentFloorData?.racks ?? []).find(r => !r.type);
    if (empty) setSelectedRack(empty.id);
    focusServerPanel();
  };
  const selectRackForMpo = () => {
    const r = (currentFloorData?.racks ?? []).find(r => r.type && !r.mpoPatchPanel);
    if (r) setSelectedRack(r.id);
    focusServerPanel();
  };
  const selectRackForTor = () => {
    const r = (currentFloorData?.racks ?? []).find(r => r.type && r.mpoPatchPanel && !r.torSwitch);
    if (r) setSelectedRack(r.id);
    focusServerPanel();
  };

  // Fraction of server traffic demand covered by external uplinks (transit + IXP)
  const locationConnectivity = useMemo(() => {
    const mmr = currentLocationData.mmr;
    if (!mmr?.built) return 0;
    const transitGbps = (mmr.transitProviders ?? []).reduce((s, tp) => s + tp.committedGbps, 0);
    const ixpGbps = (mmr.ixpConnections ?? []).reduce((s, c) => s + c.portSpeedGbps, 0);
    const externalGbps = transitGbps + ixpGbps;
    if (externalGbps === 0) return 0;
    let serverTrafficGbps = 0;
    Object.values(currentLocationData.floors ?? {}).forEach(f => {
      f.racks?.forEach(r => {
        r.servers?.forEach(s => {
          const st = SERVER_TYPES.find(x => x.id === s.type);
          if (st) serverTrafficGbps += st.trafficGbps;
        });
      });
    });
    if (serverTrafficGbps === 0) return 1;
    return Math.min(externalGbps / serverTrafficGbps, 1);
  }, [currentLocationData]);

  const ixpBonusFraction = useMemo(() => {
    const connections = currentLocationData.mmr?.ixpConnections ?? [];
    if (connections.length === 0) return 0;
    let bonus = 0;
    connections.forEach(c => {
      const ixp = IXP_EXCHANGES.find(x => x.id === c.ixpId);
      if (ixp) bonus += ixp.revenueBonus;
    });
    return Math.min(bonus, 0.20); // capped at +20%
  }, [currentLocationData]);

  return (
    <div className="lg:col-span-2">
      <ManagementPanel
        isOpen={panelOpen}
        onClose={() => setPanelOpen(false)}
        setShowUpgrades={setShowUpgrades}
        setShowPowerManagementModal={setShowPowerManagementModal}
        setShowGoals={setShowGoals}
        setShowChillerManagementModal={setShowChillerManagementModal}
        setShowSiteLayoutEditor={setShowSiteLayoutEditor}
        setShowStaffModal={setShowStaffModal}
        setShowNetworkModal={setShowNetworkModal}
        setShowMmrModal={setShowMmrModal}
        setShowFinancials={setShowFinancials}
        setShowFloorModal={setShowFloorModal}
        setShowBackboneModal={setShowBackboneModal}
        setShowContractsModal={setShowContractsModal}
        setShowGreenEnergyModal={setShowGreenEnergyModal}
        setShowLoansModal={setShowLoansModal}
        setShowFleetOverview={setShowFleetOverview}
        staffCount={staffCount}
        activeContractCount={activeContractCount}
        activeLoanCount={activeLoanCount}
        unlockHasServer={unlockSignals.hasAnyServer}
        unlockEarning={unlockSignals.isEarning}
        unlockSecondLocation={unlockSignals.hasSecondLocation}
      />

      <div className="flex justify-between items-start mb-4 gap-2 flex-wrap">
        <div>
          <h2 className="text-xl font-bold">
            {currentLocationData?.name} Datacenter
          </h2>
          <span className="text-xs text-gray-400">
            Revenue Multiplier: {(currentLocationData?.businessMultiplier || 1).toFixed(1)}x
          </span>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <NotificationSystem
            notifications={notifications}
            removeNotification={removeNotification}
          />
          <GameHeader
            gameSpeed={gameSpeed}
            setGameSpeed={setGameSpeed}
            isPaused={isPaused}
            setIsPaused={setIsPaused}
            formatCurrency={formatCurrency}
            onOpenSaveModal={onOpenSaveModal}
            onResetGame={onResetGame}
            onOpenManagement={() => { setPanelOpen(true); onManageOpened?.(); }}
            onOpenEventLog={onOpenEventLog}
            eventLogUnread={eventLogUnread}
            hasUnlockPulse={manageUnlockPulse}
          />
        </div>
      </div>

      {/* Setup checklist — hidden once all steps done */}
      <SetupChecklist
        location={currentLocationData}
        hasFloorWithRack={Object.values(currentLocationData.floors ?? {}).some(f => (f.racks ?? []).some(r => r.type))}
        setShowPowerManagementModal={setShowPowerManagementModal}
        setShowChillerManagementModal={setShowChillerManagementModal}
        onOpenMmr={() => setShowMmrView(true)}
        setShowMmrModal={setShowMmrModal}
        setShowNetworkModal={setShowNetworkModal}
        onSelectRackForPurchase={selectRackForPurchase}
        onSelectRackForMpo={selectRackForMpo}
        onSelectRackForTor={selectRackForTor}
      />

      {/* MMR popup */}
      <MmrView
        isOpen={showMmrView}
        onClose={() => setShowMmrView(false)}
        location={currentLocationData}
        currentLocation={currentLocation}
        money={money}
        buildMmr={buildMmr}
        installCoreSwitch={installCoreSwitch}
        setShowMmrModal={setShowMmrModal}
        setShowNetworkModal={setShowNetworkModal}
        formatCurrency={formatCurrency}
      />

      {/* Floor selection tabs */}
      <FloorSelector
        floors={currentLocationData.floors || {}}
        currentFloor={currentFloor}
        setCurrentFloor={setCurrentFloor}
        setShowFloorDesigner={setShowFloorDesigner}
        openDesignerForEdit={openDesignerForEdit}
        mmrBuilt={!!currentLocationData.mmr?.built}
        onOpenMmr={() => setShowMmrView(true)}
      />

      {/* Welcome message if no floors/racks */}
      {(!currentLocationData.floors || Object.keys(currentLocationData.floors).length === 0) ?
      (
        <WelcomeMessage
          locationName={currentLocationData.name}
          setSelectedRack={setSelectedRack}
          setShowFloorDesigner={setShowFloorDesigner}
        />
      ) : null}

      {/* Resource Status Displays */}
      {currentFloorData && (
        <div className="mb-4 grid grid-cols-1 md:grid-cols-2 gap-4">
          <ResourceStatusDisplay
            title="Power Status"
            used={currentLocationData.usedPower}
            available={currentLocationData.availablePowerKW}
            unit="kW"
          />
          <ResourceStatusDisplay
            title="Cooling Status"
            used={currentLocationData.usedCoolingCapacityKW}
            available={currentLocationData.totalCoolingCapacityKW}
            unit="kW"
          />
        </div>
      )}

      {/* Capacity warnings */}
      {currentFloorData && (() => {
        const powerUsed = currentLocationData.usedPower ?? 0;
        const powerAvail = currentLocationData.availablePowerKW ?? 0;
        const coolingUsed = currentLocationData.usedCoolingCapacityKW ?? 0;
        const coolingAvail = currentLocationData.totalCoolingCapacityKW ?? 0;
        const powerRatio = powerAvail > 0 ? powerUsed / powerAvail : 0;
        const coolingRatio = coolingAvail > 0 ? coolingUsed / coolingAvail : 0;
        const rackCount = currentFloorData.racks?.length ?? 0;
        const rackCapacity = (currentFloorData.designGrid?.flat().filter(c => c?.type === 'rack').length) ?? 0;
        const warnings: { msg: string; critical: boolean }[] = [];
        if (powerAvail > 0 && powerRatio >= 0.9) warnings.push({ msg: `Power at ${Math.round(powerRatio * 100)}% — add capacity before installing more servers`, critical: powerRatio >= 0.98 });
        if (coolingAvail > 0 && coolingRatio >= 0.85) warnings.push({ msg: `Cooling at ${Math.round(coolingRatio * 100)}% — upgrade chillers or floor quality`, critical: coolingRatio >= 0.95 });
        if (rackCapacity > 0 && rackCount >= rackCapacity) warnings.push({ msg: `All rack slots occupied on this floor — design a new floor to expand`, critical: false });
        if (warnings.length === 0) return null;
        return (
          <div className="mb-3 space-y-1">
            {warnings.map((w, i) => (
              <div key={i} className={`flex items-center gap-2 text-xs px-3 py-1.5 rounded-lg border ${w.critical ? 'bg-red-950/50 border-red-700 text-red-300' : 'bg-amber-950/50 border-amber-700 text-amber-300'}`}>
                <span className={`font-bold ${w.critical ? 'text-red-400' : 'text-amber-400'}`}>{w.critical ? '⚠' : '!'}</span>
                {w.msg}
              </div>
            ))}
          </div>
        );
      })()}

      {/* Rack Grid */}
      <RackGrid
        racks={currentFloorData?.racks || []}
        selectedRack={selectedRack}
        setSelectedRack={setSelectedRack}
        rackTypes={rackTypes}
        floor={currentFloorData}
      />

      {/* Server Management */}
      <div
        ref={serverPanelRef}
        className={`rounded-lg transition-all duration-500 ${panelFlash ? 'ring-2 ring-blue-400 ring-offset-2 ring-offset-gray-900' : 'ring-0'}`}
      >
      <ServerManagement
        selectedRack={selectedRack}
        selectedRackObj={selectedRackObj}
        selectedRackType={selectedRackType}
        selectedRackUsedPower={selectedRackUsedPower}
        money={money}
        currentLocation={currentLocation}
        buyRack={buyRack}
        installServer={installServer}
        fillRack={fillRack}
        installTORSwitch={installTORSwitch}
        installMpoPatchPanel={installMpoPatchPanel}
        upgradeMpoPatchPanel={upgradeMpoPatchPanel}
        installRedundantTorSwitch={installRedundantTorSwitch}
        installManagementSwitch={installManagementSwitch}
        removeServer={removeServer}
        removeRack={removeRack}
        repairServer={repairServer}
        gameTime={gameTime}
        rackTypes={rackTypes}
        serverTypes={serverTypes}
        torSwitchTypes={torSwitchTypes}
        playerLevel={playerLevel}
        availablePower={Math.max(0, (currentLocationData.availablePowerKW || 0) - (currentLocationData.usedPower || 0))}
        hasTransformer={!!currentLocationData.transformer}
        hasPowerhouse={!!currentLocationData.powerhouse?.installed}
        hasUps={!!currentLocationData.ups?.installed}
        hasPowerSupplier={!!currentLocationData.selectedPowerSupplierId}
        hasCooling={(currentLocationData.totalCoolingCapacityKW || 0) > 0}
        availableCooling={Math.max(0, (currentLocationData.totalCoolingCapacityKW || 0) - (currentLocationData.usedCoolingCapacityKW || 0))}
        locationConnectivity={locationConnectivity}
        networkStatus={{
          mmrBuilt: !!currentLocationData.mmr?.built,
          locationHasCore: !!currentLocationData.coreSwitch,
          floorHasHda: !!currentFloorData?.hdaSwitch,
          externalConnectivity: locationConnectivity,
        }}
        thermalStrain={(currentLocationData.totalCoolingCapacityKW || 0) > 0
          ? (currentLocationData.usedCoolingCapacityKW || 0) / currentLocationData.totalCoolingCapacityKW
          : 0}
        isPaused={isPaused}
        selectedPowerSupplierId={currentLocationData.selectedPowerSupplierId}
        formatCurrency={formatCurrency}
        reputation={reputation}
        powerEfficiencyUpgrade={powerEfficiencyUpgrade}
        serverPerformanceUpgrade={serverPerformanceUpgrade}
        ixpBonusFraction={ixpBonusFraction}
        activeContracts={activeContracts}
        pendingDeliveries={pendingDeliveries}
        gameDay={gameDay}
      />
      </div>

    </div>
  );
};

export default DatacenterView;
